import { useEffect, useMemo, useRef, useState } from 'react'
import {
  canScheduleFor,
  clashesFromError,
  useCancelMeeting,
  useCheckClashes,
  useCompanies,
  useCreateMeeting,
  useDirectory,
  useMeeting,
  useUpdateMeeting,
} from '../api/hooks'
import { useAuth } from '../auth/AuthProvider'
import type { MeetingClash } from '../api/types'
import {
  addDaysToDateInputValue,
  daysBetweenDateInputValues,
  formatDateTime,
  formatMeetingWhen,
  formatTimeRange,
  fromDateAndTimeInputValues,
  toDateInputValue,
  toTimeInputValue,
} from '../lib/dates'
import { Avatar } from './TaskRow'
import { Button, ErrorNotice, Loading, MeetingStatusPill, cx } from './ui'

/** Long enough that picking a time does not fire a request per keystroke. */
const CHECK_DEBOUNCE_MS = 400

/** Mirrors the server's minimum, so a short reason never costs a round trip. */
const MIN_REASON_LENGTH = 10

const DEFAULT_START = '09:00'
const DEFAULT_END = '10:00'

/**
 * "Ana", "Ana and Lex", "Ana, Lex and Sam". Spelled out rather than joined with
 * slashes, because this sentence is read under time pressure.
 */
function nameList(names: string[]): string {
  if (names.length <= 1) return names[0] ?? 'Someone'
  const last = names[names.length - 1] ?? ''
  return `${names.slice(0, -1).join(', ')} and ${last}`
}

/**
 * One clash, as a sentence. A clash the viewer may not see keeps the time and
 * the names she chose herself and says nothing else, and it is deliberately not
 * a link: there is nothing she is allowed to open.
 */
function clashSentence(clash: MeetingClash): string {
  const names = nameList(clash.attendees.map((a) => a.name))
  const verb = clash.attendees.length > 1 ? 'are' : 'is'
  const when = formatTimeRange(clash.startsAt, clash.endsAt)
  if (clash.redacted) return `${names} ${verb} already booked ${when}. ${clash.title}.`
  const company = clash.company.shortName ?? clash.company.name
  return `${names} ${verb} already booked ${when} (${clash.title}, ${company}).`
}

/** All the attendee picker needs, from the directory or from the meeting itself. */
type AttendeeOption = { id: string; name: string; email: string; isActive: boolean }

/** All the company select needs. The meeting's own company may not be in the list. */
type CompanyOption = { id: string; name: string }

/**
 * Slide-over for one meeting. It serves both booking a new meeting and viewing
 * or editing an existing one, because the two are the same seven fields and the
 * same clash check; `meetingId` of null is the only difference.
 */
export function MeetingDrawer({
  meetingId,
  initialDate,
  onClose,
}: {
  /** Null books a new meeting. */
  meetingId: string | null
  /** YYYY-MM-DD, prefilled when the booking started from a day cell. */
  initialDate?: string
  onClose: () => void
}) {
  const isCreate = meetingId === null
  const { user } = useAuth()
  const { data, isLoading, error } = useMeeting(meetingId)
  const { data: companies } = useCompanies()
  const { data: people } = useDirectory()

  const createMeeting = useCreateMeeting()
  const updateMeeting = useUpdateMeeting()
  const cancelMeeting = useCancelMeeting()
  const checkClashes = useCheckClashes()

  const meeting = data?.meeting ?? null
  const canWrite = isCreate ? true : (data?.permissions.canWrite ?? false)

  const [title, setTitle] = useState('')
  const [companyId, setCompanyId] = useState('')
  const [date, setDate] = useState(initialDate ?? '')
  const [startTime, setStartTime] = useState(DEFAULT_START)
  /**
   * The end has a date of its own. A meeting is stored in UTC and shown in the
   * viewer's own zone, so one that starts at 10:30 in Bangkok starts at 23:30
   * the previous day in New York and ends at 00:30 the day after that. With a
   * single date field that meeting reads as ending 23 hours before it begins,
   * which made it impossible to save, impossible to move off a clash, and left
   * a validation error standing over a perfectly good booking.
   */
  const [endDate, setEndDate] = useState(initialDate ?? '')
  const [endTime, setEndTime] = useState(DEFAULT_END)
  const [attendeeIds, setAttendeeIds] = useState<string[]>([])
  const [location, setLocation] = useState('')
  const [notes, setNotes] = useState('')

  const [clashes, setClashes] = useState<MeetingClash[]>([])
  const [overriding, setOverriding] = useState(false)
  const [reason, setReason] = useState('')
  const [confirmingCancel, setConfirmingCancel] = useState(false)

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose])

  // Fill the form from the loaded meeting. Keyed on updatedAt as well as id so a
  // save that leaves the drawer open shows what the server actually stored.
  useEffect(() => {
    if (!meeting) return
    setTitle(meeting.title)
    setCompanyId(meeting.company.id)
    setDate(toDateInputValue(meeting.startsAt))
    setStartTime(toTimeInputValue(meeting.startsAt))
    setEndDate(toDateInputValue(meeting.endsAt))
    setEndTime(toTimeInputValue(meeting.endsAt))
    setAttendeeIds(meeting.attendees.map((a) => a.id))
    setLocation(meeting.location ?? '')
    setNotes(meeting.notes ?? '')
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [meeting?.id, meeting?.updatedAt])

  // Only companies the viewer may book for, plus the meeting's own company when
  // editing: the creator of a meeting can still edit it after losing the right,
  // and dropping the company out of the select would silently move the meeting.
  // The owning company is taken from the meeting rather than looked up in the
  // list, because someone who is only an attendee never sees that company on
  // /api/companies at all and the field would otherwise render blank under a
  // header naming it.
  const companyOptions = useMemo<CompanyOption[]>(() => {
    const all = companies ?? []
    const allowed = user ? all.filter((c) => canScheduleFor(c, user)) : []
    const options = allowed.map((c) => ({ id: c.id, name: c.name }))
    if (!meeting || options.some((c) => c.id === meeting.company.id)) return options
    return [{ id: meeting.company.id, name: meeting.company.name }, ...options]
  }, [companies, user, meeting])

  const onlyCompanyId = companyOptions.length === 1 ? companyOptions[0]?.id : undefined
  useEffect(() => {
    if (!isCreate || !onlyCompanyId) return
    setCompanyId((prev) => prev || onlyCompanyId)
  }, [isCreate, onlyCompanyId])

  // Blank until a date is picked, and never out of step with the start date.
  const effectiveEndDate = endDate || date
  const spansDays = effectiveEndDate !== date

  const startsAt = fromDateAndTimeInputValues(date, startTime)
  const endsAt = fromDateAndTimeInputValues(effectiveEndDate, endTime)
  const windowValid =
    startsAt !== null && endsAt !== null && new Date(startsAt).getTime() < new Date(endsAt).getTime()
  const endBeforeStart = startsAt !== null && endsAt !== null && !windowValid

  /** Moving the date drags the end with it, so a two day span stays two days. */
  function changeDate(value: string) {
    const span = daysBetweenDateInputValues(date, effectiveEndDate)
    setDate(value)
    setEndDate(span === 0 ? value : addDaysToDateInputValue(value, span))
  }

  /**
   * Who can be ticked: the directory, plus everyone already on this meeting.
   * The second half is not a nicety. /api/users returns only active people the
   * caller shares work with, so an attendee who has since left, or who belongs
   * to an entity the caller does not, would have no row at all while their id
   * still rode along on every save. The meeting became unsaveable with no way
   * to take them off it.
   */
  const attendeeOptions = useMemo<AttendeeOption[]>(() => {
    const byId = new Map<string, AttendeeOption>()
    for (const person of people ?? []) byId.set(person.id, person)
    for (const person of meeting?.attendees ?? []) {
      if (!byId.has(person.id)) byId.set(person.id, person)
    }
    return [...byId.values()].sort((a, b) => a.name.localeCompare(b.name))
  }, [people, meeting])

  function toggleAttendee(id: string) {
    setAttendeeIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]))
  }

  // A stale answer must never overwrite a fresh one, and a 409 must never be
  // wiped by a check that was already in flight when it arrived.
  const checkSeq = useRef(0)
  const checkRef = useRef(checkClashes.mutate)
  useEffect(() => {
    checkRef.current = checkClashes.mutate
  })

  useEffect(() => {
    if (!canWrite || !companyId || !windowValid || attendeeIds.length === 0) {
      checkSeq.current += 1
      setClashes((prev) => (prev.length === 0 ? prev : []))
      return
    }
    const seq = ++checkSeq.current
    const timer = window.setTimeout(() => {
      checkRef.current(
        {
          companyId,
          startsAt: startsAt as string,
          endsAt: endsAt as string,
          attendeeIds,
          excludeMeetingId: meetingId ?? undefined,
        },
        {
          onSuccess: (result) => {
            if (seq === checkSeq.current) setClashes(result.clashes)
          },
          // A check that failed proves nothing, least of all that the old answer
          // still holds. Leaving the previous clash on screen would keep Save
          // disabled over a slot that is now free, with no way out but inventing
          // an override reason for a clash that does not exist. Clear it and let
          // the save through: the server re-checks inside the write and answers
          // 409 if a clash really is there, which is what the line under this
          // block already promises.
          onError: () => {
            if (seq === checkSeq.current) setClashes([])
          },
        },
      )
    }, CHECK_DEBOUNCE_MS)
    return () => window.clearTimeout(timer)
  }, [canWrite, companyId, startsAt, endsAt, windowValid, attendeeIds, meetingId])

  // Nothing to book over means nothing to justify.
  useEffect(() => {
    if (clashes.length > 0) return
    setOverriding(false)
    setReason('')
  }, [clashes])

  const reasonValid = reason.trim().length >= MIN_REASON_LENGTH
  const overrideReady = overriding && reasonValid
  const blockedByClash = clashes.length > 0 && !overrideReady

  const canSubmit =
    canWrite &&
    title.trim().length > 0 &&
    companyId !== '' &&
    windowValid &&
    attendeeIds.length > 0 &&
    !blockedByClash

  const saving = createMeeting.isPending || updateMeeting.isPending

  // A 409 is a clash, and it belongs in the clash block. Everything else is a
  // plain failure and goes to ErrorNotice, so the two never both fire.
  const rawSaveError = createMeeting.error ?? updateMeeting.error
  const saveError = rawSaveError && clashesFromError(rawSaveError) ? null : rawSaveError

  async function handleSave(event: React.FormEvent) {
    event.preventDefault()
    if (!canSubmit || !startsAt || !endsAt) return

    const payload = {
      title: title.trim(),
      companyId,
      startsAt,
      endsAt,
      attendeeIds,
      location: location.trim() || null,
      notes: notes.trim() || null,
      ...(overrideReady ? { overrideReason: reason.trim() } : {}),
    }

    try {
      if (isCreate) await createMeeting.mutateAsync(payload)
      else await updateMeeting.mutateAsync({ id: meetingId, ...payload })
      onClose()
    } catch (err) {
      const blocking = clashesFromError(err)
      if (!blocking) return
      // Somebody booked in the gap between the check and the save. Same block,
      // same "Book anyway", no second error path.
      checkSeq.current += 1
      setClashes(blocking)
      setOverriding(false)
      setReason('')
    }
  }

  async function handleCancelMeeting() {
    if (!meetingId) return
    await cancelMeeting.mutateAsync(meetingId)
    setConfirmingCancel(false)
  }

  const heading = isCreate ? 'New meeting' : (meeting?.title ?? 'Meeting')
  const showForm = isCreate || Boolean(meeting)

  return (
    <div className="fixed inset-0 z-40 flex justify-end">
      <button
        type="button"
        aria-label="Close panel"
        onClick={onClose}
        className="absolute inset-0 bg-slate-900/30 backdrop-blur-[1px]"
      />

      <aside
        role="dialog"
        aria-modal="true"
        aria-label={isCreate ? 'New meeting' : 'Meeting details'}
        className="relative flex h-full w-full max-w-xl flex-col overflow-y-auto border-l border-slate-200 bg-white shadow-2xl dark:border-slate-800 dark:bg-slate-900"
      >
        <header className="sticky top-0 z-10 flex items-start gap-3 border-b border-slate-200 bg-white/95 px-5 py-4 backdrop-blur dark:border-slate-800 dark:bg-slate-900/95">
          <div className="min-w-0 flex-1">
            {meeting && (
              <p className="mb-1 truncate text-xs text-slate-500 dark:text-slate-400">
                {meeting.company.name}
              </p>
            )}
            <div className="flex items-center gap-2">
              <h2 className="truncate text-lg font-semibold tracking-tight">{heading}</h2>
              {meeting && <MeetingStatusPill status={meeting.status} />}
            </div>
            {/* Read-only viewers get no editable fields, so without this line
                the panel never says when the meeting actually is. */}
            {meeting && (
              <p className="mt-1 truncate text-xs text-slate-600 dark:text-slate-300">
                {formatMeetingWhen(meeting.startsAt, meeting.endsAt)}
              </p>
            )}
          </div>

          <Button variant="ghost" size="sm" onClick={onClose} aria-label="Close">
            <svg viewBox="0 0 20 20" fill="currentColor" className="h-4 w-4">
              <path d="M6.3 5 5 6.3 8.6 10 5 13.7 6.3 15 10 11.4l3.7 3.6 1.3-1.3-3.6-3.7 3.6-3.7L13.7 5 10 8.6 6.3 5Z" />
            </svg>
          </Button>
        </header>

        <div className="flex-1 px-5 py-4">
          {isLoading && <Loading />}
          {error && <ErrorNotice error={error} />}

          {showForm && (
            <form onSubmit={handleSave}>
              {!canWrite && (
                <p className="mb-4 rounded-lg bg-slate-100 px-3 py-2 text-xs text-slate-600 dark:bg-slate-800 dark:text-slate-400">
                  You have read-only access to this meeting. Ask the person who booked it, or an
                  administrator, to make a change.
                </p>
              )}

              {isCreate && companyOptions.length === 0 && (
                <p className="mb-4 rounded-lg bg-amber-50 px-3 py-2 text-sm text-amber-800 dark:bg-amber-950/50 dark:text-amber-300">
                  You are not set up to book meetings for any company yet. Ask your administrator to
                  give you the scheduling right.
                </p>
              )}

              <div className="grid gap-4 sm:grid-cols-2">
                <div className="sm:col-span-2">
                  <label className="label" htmlFor="m-title">
                    Title
                  </label>
                  <input
                    id="m-title"
                    className="field"
                    value={title}
                    disabled={!canWrite}
                    autoFocus={isCreate}
                    placeholder="What is the meeting about?"
                    onChange={(e) => setTitle(e.target.value)}
                  />
                </div>

                <div className="sm:col-span-2">
                  <label className="label" htmlFor="m-company">
                    Company
                  </label>
                  <select
                    id="m-company"
                    className="field"
                    value={companyId}
                    disabled={!canWrite}
                    onChange={(e) => setCompanyId(e.target.value)}
                  >
                    <option value="">Choose…</option>
                    {companyOptions.map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="sm:col-span-2">
                  <label className="label" htmlFor="m-date">
                    Date
                  </label>
                  <input
                    id="m-date"
                    type="date"
                    className="field"
                    value={date}
                    disabled={!canWrite}
                    onChange={(e) => changeDate(e.target.value)}
                  />
                </div>

                <div>
                  <label className="label" htmlFor="m-start">
                    Starts
                  </label>
                  <input
                    id="m-start"
                    type="time"
                    className="field"
                    value={startTime}
                    disabled={!canWrite}
                    onChange={(e) => setStartTime(e.target.value)}
                  />
                </div>

                <div>
                  <label className="label" htmlFor="m-end">
                    Ends
                  </label>
                  <input
                    id="m-end"
                    type="time"
                    className="field"
                    value={endTime}
                    disabled={!canWrite}
                    onChange={(e) => setEndTime(e.target.value)}
                  />
                </div>

                {/* Only when it is actually doing something: a meeting that runs
                    past midnight in the viewer's own zone, or an end that has
                    landed before its start and needs a day to fix it. */}
                {(spansDays || endBeforeStart) && (
                  <div className="sm:col-span-2">
                    <label className="label" htmlFor="m-end-date">
                      Ends on
                    </label>
                    <input
                      id="m-end-date"
                      type="date"
                      className="field"
                      value={effectiveEndDate}
                      disabled={!canWrite}
                      onChange={(e) => setEndDate(e.target.value)}
                    />
                  </div>
                )}
              </div>

              {endBeforeStart && (
                <p className="mt-2 text-sm text-red-600 dark:text-red-400">
                  The end has to be after the start. If the meeting runs past midnight, move the
                  end date on by a day.
                </p>
              )}

              {/* The clash block. Everything about a double booking lands here,
                  whether it came from the live check or from a 409 on save. */}
              {clashes.length > 0 && (
                <div
                  role="alert"
                  className="mt-3 rounded-lg border border-amber-300 bg-amber-50 px-3 py-2.5 text-sm text-amber-900 dark:border-amber-900 dark:bg-amber-950/50 dark:text-amber-200"
                >
                  <p className="font-medium">
                    {clashes.length === 1
                      ? 'This time is already taken'
                      : 'This time is already taken more than once'}
                  </p>
                  <ul className="mt-1.5 space-y-1">
                    {clashes.map((clash) => (
                      <li key={clash.meetingId}>{clashSentence(clash)}</li>
                    ))}
                  </ul>

                  {!overriding ? (
                    <div className="mt-2.5 flex flex-wrap items-center gap-2">
                      <span className="text-xs text-amber-800 dark:text-amber-300">
                        Move the meeting, or book over it and say why.
                      </span>
                      <Button
                        type="button"
                        size="sm"
                        disabled={!canWrite}
                        onClick={() => setOverriding(true)}
                      >
                        Book anyway
                      </Button>
                    </div>
                  ) : (
                    <div className="mt-2.5">
                      <label className="label" htmlFor="m-reason">
                        Why are you booking over this?
                      </label>
                      <textarea
                        id="m-reason"
                        className="field min-h-16"
                        value={reason}
                        autoFocus
                        placeholder="Agreed with both diaries, the earlier meeting is moving."
                        onChange={(e) => setReason(e.target.value)}
                      />
                      <p
                        className={cx(
                          'mt-1 text-xs',
                          reasonValid
                            ? 'text-amber-800 dark:text-amber-300'
                            : 'text-red-700 dark:text-red-400',
                        )}
                      >
                        {reasonValid
                          ? 'This is recorded against your name in the activity log.'
                          : `Give a reason of at least ${MIN_REASON_LENGTH} characters. It is recorded against your name in the activity log.`}
                      </p>
                      <Button
                        type="button"
                        size="sm"
                        className="mt-2"
                        onClick={() => {
                          setOverriding(false)
                          setReason('')
                        }}
                      >
                        Pick another time instead
                      </Button>
                    </div>
                  )}
                </div>
              )}

              {checkClashes.isError && (
                <p className="mt-2 text-xs text-slate-500 dark:text-slate-400">
                  Could not check for clashes just now. The save is checked again on the server.
                </p>
              )}

              <div className="mt-5">
                <p className="label" id="m-attendees-label">
                  Attendees
                </p>
                <div
                  role="group"
                  aria-labelledby="m-attendees-label"
                  className="max-h-56 overflow-y-auto rounded-lg border border-slate-300 dark:border-slate-700"
                >
                  {attendeeOptions.length === 0 && (
                    <p className="px-3 py-3 text-sm text-slate-500 dark:text-slate-400">
                      Nobody to invite yet.
                    </p>
                  )}
                  {attendeeOptions.map((person) => (
                    <label
                      key={person.id}
                      className="row-alt flex cursor-pointer items-center gap-2.5 px-3 py-2 text-sm hover:bg-slate-50 dark:hover:bg-slate-800/50"
                    >
                      <input
                        type="checkbox"
                        className="rounded border-slate-300 dark:border-slate-600"
                        checked={attendeeIds.includes(person.id)}
                        disabled={!canWrite}
                        onChange={() => toggleAttendee(person.id)}
                      />
                      <Avatar name={person.name} />
                      <span className="min-w-0 flex-1 truncate">
                        {person.name}
                        {!person.isActive && (
                          <span className="ml-1.5 text-xs text-slate-500 dark:text-slate-400">
                            (deactivated)
                          </span>
                        )}
                      </span>
                      <span className="hidden truncate text-xs text-slate-500 sm:block dark:text-slate-400">
                        {person.email}
                      </span>
                    </label>
                  ))}
                </div>
                <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                  {attendeeIds.length === 0
                    ? 'Pick at least one person.'
                    : `${attendeeIds.length} invited.`}
                </p>
              </div>

              <div className="mt-5 grid gap-4">
                <div>
                  <label className="label" htmlFor="m-location">
                    Location
                  </label>
                  <input
                    id="m-location"
                    className="field"
                    value={location}
                    disabled={!canWrite}
                    placeholder="Room, address or video link"
                    onChange={(e) => setLocation(e.target.value)}
                  />
                </div>

                <div>
                  <label className="label" htmlFor="m-notes">
                    Notes
                  </label>
                  <textarea
                    id="m-notes"
                    className="field min-h-20"
                    value={notes}
                    disabled={!canWrite}
                    placeholder={canWrite ? 'Anything the attendees need to know' : 'No notes'}
                    onChange={(e) => setNotes(e.target.value)}
                  />
                </div>
              </div>

              {saveError && (
                <div className="mt-3">
                  <ErrorNotice error={saveError} />
                </div>
              )}

              <div className="mt-5 flex flex-wrap gap-2">
                {canWrite && (
                  <Button type="submit" variant="primary" loading={saving} disabled={!canSubmit}>
                    {isCreate ? 'Book meeting' : 'Save changes'}
                  </Button>
                )}
                <Button type="button" onClick={onClose}>
                  Close
                </Button>
              </div>

              {meeting && (
                <dl className="mt-6 grid gap-1 border-t border-slate-200 pt-4 text-xs text-slate-500 dark:border-slate-800 dark:text-slate-400">
                  <div className="flex gap-2">
                    <dt>Booked by</dt>
                    <dd className="font-medium text-slate-700 dark:text-slate-300">
                      {meeting.createdBy.name}
                    </dd>
                    <dd>· {formatDateTime(meeting.createdAt)}</dd>
                  </div>
                </dl>
              )}

              {meeting && canWrite && meeting.status === 'SCHEDULED' && (
                <footer className="mt-6 border-t border-slate-200 pt-4 dark:border-slate-800">
                  {confirmingCancel ? (
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="text-sm text-slate-600 dark:text-slate-400">
                        Cancel this meeting for everyone?
                      </span>
                      <Button
                        type="button"
                        variant="danger"
                        size="sm"
                        loading={cancelMeeting.isPending}
                        onClick={handleCancelMeeting}
                      >
                        Yes, cancel it
                      </Button>
                      <Button type="button" size="sm" onClick={() => setConfirmingCancel(false)}>
                        Keep it
                      </Button>
                    </div>
                  ) : (
                    <Button
                      type="button"
                      variant="danger"
                      size="sm"
                      onClick={() => setConfirmingCancel(true)}
                    >
                      Cancel meeting
                    </Button>
                  )}
                  <p className="mt-2 text-xs text-slate-500 dark:text-slate-400">
                    A cancelled meeting stays on the record and stops blocking the time.
                  </p>
                  {cancelMeeting.error && (
                    <div className="mt-2">
                      <ErrorNotice error={cancelMeeting.error} />
                    </div>
                  )}
                </footer>
              )}
            </form>
          )}
        </div>
      </aside>
    </div>
  )
}
